import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Starter } from '@couch/shared';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, errorMessage } from '../../api/client';
import { MACHINES_KEY } from '../machines/MachineActions';
import { RECIPES_KEY } from '../RecipesPage';

function StarterCard({ starter, chosen, onChoose, machineId }: { starter: Starter; chosen: boolean; onChoose: () => void; machineId: string }) {
  return (
    <label className={`starter-card${chosen ? ' starter-chosen' : ''}${starter.ready ? '' : ' starter-missing'}`}>
      <input type="radio" name="family" value={starter.family} checked={chosen} disabled={!starter.ready} onChange={onChoose} />
      <strong>{starter.title} {starter.kind === 'video' && <span className="badge">Video</span>} {starter.paid && <span className="badge">Comfy.org credits</span>}</strong>
      <span className="muted">{starter.description}</span>
      {starter.ready && starter.files.length > 0 && <span className="muted">Uses {starter.files.map((f) => f.choices[0]).join(' + ')}</span>}
      {starter.ready && starter.paid && <span className="muted">Runs in the cloud and is paid from your Comfy.org account.</span>}
      {!starter.ready && (
        <span>Needs {starter.missing.map((m) => m.title).join(' and ')} —{' '}
          {starter.missing.some((m) => m.where === 'machine') && <Link to={`/machines/${machineId}`}>machine page</Link>}
          {starter.missing.some((m) => m.where === 'machine') && starter.missing.some((m) => m.where === 'account') && ', '}
          {starter.missing.some((m) => m.where === 'account') && <Link to="/account">Account → Model sources</Link>}
        </span>
      )}
    </label>
  );
}

/** "New recipe" from per-family starting points with the machine's own models (doc 29). */
export function NewRecipePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const machines = useQuery({ queryKey: MACHINES_KEY, queryFn: api.listMachines });
  const [machineOverride, setMachineOverride] = useState('');
  const [familyOverride, setFamilyOverride] = useState('');
  const [prompt, setPrompt] = useState('');
  const online = (machines.data?.machines ?? []).filter((m) => m.online);
  const machineId = machineOverride || online[0]?.id || '';
  const starters = useQuery({ queryKey: ['starters', machineId], queryFn: () => api.machineStarters(machineId), enabled: machineId !== '' });
  const family = familyOverride || starters.data?.starters.find((s) => s.ready)?.family || '';
  const create = useMutation({
    mutationFn: () => api.recipeFromStarter({ machineId, family: family as Starter['family'], prompt }),
    onSuccess: async ({ recipe }) => { await queryClient.invalidateQueries({ queryKey: RECIPES_KEY }); await navigate(`/workflows/${recipe.id}`); },
  });
  return (
    <section aria-labelledby="new-title">
      <p><Link to="/workflows">← Workflows</Link></p>
      <h1 id="new-title">New workflow</h1>
      <p className="muted">Pick a model family for a picture, or <Link to="/workflows/import">start from one of ComfyUI's own workflows or a workflow file</Link> — video, editing, poses, upscaling and more.</p>
      {machines.isSuccess && online.length === 0 && <p className="notice">No machine is online. Switch one on to see what it can make.</p>}
      <div className="recipe-pickers">
        <label>Machine{' '}
          <select value={machineId} onChange={(e) => { setMachineOverride(e.target.value); setFamilyOverride(''); }}>
            {online.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </label>
      </div>
      {starters.isPending && machineId !== '' && <p role="status">Looking at the models on this machine…</p>}
      {starters.isError && <p role="alert" className="form-error">{errorMessage(starters.error)}</p>}
      <fieldset className="starter-grid" aria-label="Model family">
        {starters.data?.starters.map((s) => <StarterCard key={s.family} starter={s} machineId={machineId} chosen={s.family === family} onChoose={() => { setFamilyOverride(s.family); }} />)}
      </fieldset>
      <form className="panel" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
        <label htmlFor="new-prompt">What should the picture show?</label>
        <textarea id="new-prompt" rows={3} value={prompt} onChange={(e) => { setPrompt(e.target.value); }} placeholder="a foggy harbour at dawn, fishing boats, analogue photo" />
        {create.isError && <p role="alert" className="form-error">{errorMessage(create.error)}</p>}
        <p><button type="submit" disabled={!family || !prompt.trim() || create.isPending}>Create workflow</button></p>
      </form>
    </section>
  );
}
